import DadosIniciais from '../ModulosCrefito/DadosIniciais'
import DadosEstabelecimento from '../ModulosCrefito/DadosEstabelecimento'
import Livre from './TiposTO/Livre'
import Consultorio from './TiposTO/Consultorio'
import Caps from './TiposTO/Caps'
import Clinica from './TiposTO/Clinica'
import Nasf from './TiposTO/Nasf'
import ExercicioIlegal from './TiposTO/ExercicioIlegal'
import RedeSocial from './TiposTO/RedeSocial'
import Ilpis from './TiposTO/Ilpis'
import Hospital from './TiposTO/Hospital'
import Cer from './TiposTO/Cer'
import Fechado from './Fechado'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import AiContainer from '../AIs/AisTo/AiContainer'

import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { ficha } from '../../../state/slices/formSlice'

// ajuste o path conforme sua estrutura
import { createLocksClient } from './locksClient'

const AUTO_SAVE_MS = 40000      // 40s
const TOAST_TTL_MS = 3000       // 3s

const EditTO = () => {
    const [tipo, setTipo] = useState()
    const [ready, setReady] = useState(false)

    // Lock/UI
    const [locked, setLocked] = useState(false)
    const [lockedBy, setLockedBy] = useState(null)
    const [lockedUntil, setLockedUntil] = useState(null)
    const heartbeatStopRef = useRef(null)
    const retryTimerRef = useRef(null)
    const locksRef = useRef(null)

    // Toast
    const [toast, setToast] = useState(null) // { type: 'success'|'warn'|'error', text, ts }
    const toastTimerRef = useRef(null)

    const id = window.location.pathname.slice(8)
    const resource = `tvto:${id}`

    const dispatch = useDispatch()
    const qtd = useSelector((state) => state.addForm.qtdModulos)
    const report = useSelector((state) => state.addForm.modulos)

    // refs para valores atuais
    const qtdRef = useRef(qtd)
    const reportRef = useRef(report)
    const readyRef = useRef(ready)
    const lockedRef = useRef(locked)
    const sessionIdRef = useRef(null)
    const lastSavedHashRef = useRef(null) // opcional

    useEffect(() => { qtdRef.current = qtd }, [qtd])
    useEffect(() => { reportRef.current = report }, [report])
    useEffect(() => { readyRef.current = ready }, [ready])
    useEffect(() => { lockedRef.current = locked }, [locked])

    // Toast helpers
    const showToast = useCallback((type, text) => {
        setToast({ type, text, ts: Date.now() })
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
        toastTimerRef.current = setTimeout(() => setToast(null), TOAST_TTL_MS)
    }, [])
    useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current) }, [])

    // Init locks client (sessionId via body/params)
    useEffect(() => {
        const locks = createLocksClient(resource)
        locksRef.current = locks
        sessionIdRef.current = locks.sessionId
        return () => { }
    }, [resource])

    // Carrega dados iniciais
    useEffect(() => {
        const url = '/api/tvs/getTvTo'
        axios.post(url, { id })
            .then(res => {
                const report = res.data

                // 1) Normaliza campos "únicos"
                const normalized = {
                    ...report,
                    dadosIniciais: report.dadosIniciais?.length ? report.dadosIniciais : [{}],
                    dadosEstabelecimento: report.dadosEstabelecimento?.length ? report.dadosEstabelecimento : [{}],
                    dificuldades: report.dificuldades?.length ? report.dificuldades : [{}],
                }

                // 2) Garante arrays
                const ensureArr = (v) => (Array.isArray(v) ? v : (v ? [v] : []))
                normalized.aito = ensureArr(report.aito)

                // 3) Deriva quantidades
                const quantidades = Object.keys(normalized).reduce((acc, k) => {
                    const v = normalized[k]
                    acc[k] = Array.isArray(v) ? v.length : 0
                    return acc
                }, {})

                // 4) Redux
                dispatch(ficha({ ...normalized, quantidades: [quantidades] }))

                // 5) Estado local
                setTipo(normalized.tipo || '')
                setReady(true)

                try {
                    lastSavedHashRef.current = JSON.stringify({ qtd: qtdRef.current, report: reportRef.current })
                } catch { }
            })
            .catch(error => {
                console.log(error)
                setReady(true)
            })
    }, [id, dispatch])

    // Heartbeat
    const startHeartbeat = useCallback(() => {
        if (!locksRef.current) return
        if (heartbeatStopRef.current) return
        const hb = locksRef.current.heartbeat({
            onRenewOk: () => {
                setLocked(false)
                setLockedBy(null)
                setLockedUntil(new Date(Date.now() + locksRef.current.ttlMs))
            },
            onRenewFail: (info) => {
                if (info && info.locked) {
                    setLocked(true)
                    setLockedBy(info.by || null)
                    setLockedUntil(info.expiresAt ? new Date(info.expiresAt) : null)
                    showToast('warn', 'Este documento está em edição por outro usuário.')
                }
            }
        })
        heartbeatStopRef.current = hb.stop
    }, [showToast])

    const stopHeartbeat = useCallback(() => {
        if (heartbeatStopRef.current) {
            heartbeatStopRef.current()
            heartbeatStopRef.current = null
        }
    }, [])

    // Acquire / Release
    const acquireLock = useCallback(async () => {
        if (!locksRef.current) return false
        try {
            const r = await locksRef.current.acquire()
            if (r.ok) {
                setLocked(false)
                setLockedBy(null)
                setLockedUntil(new Date(Date.now() + locksRef.current.ttlMs))
                showToast('success', 'Você assumiu a edição deste documento.')
                startHeartbeat()
                return true
            }
            // bloqueado por outro
            setLocked(true)
            setLockedBy(r.by || null)
            setLockedUntil(r.expiresAt ? new Date(r.expiresAt) : null)
            showToast('warn', 'Este documento está sendo editado por outra pessoa.')
            stopHeartbeat()
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
            const retryMs = (r.waitMs ?? locksRef.current.ttlMs) + 1000
            retryTimerRef.current = setTimeout(() => { acquireLock() }, retryMs)
            return false
        } catch (err) {
            console.error('[acquireLock TO] erro:', err)
            showToast('error', 'Falha ao assumir a edição.')
            return false
        }
    }, [showToast, startHeartbeat, stopHeartbeat])

    const releaseLock = useCallback(async () => {
        try {
            stopHeartbeat()
            if (locksRef.current) await locksRef.current.release()
        } catch { }
    }, [stopHeartbeat])

    // Montagem / Desmontagem
    useEffect(() => {
        acquireLock()
        const onBeforeUnload = () => { releaseLock() }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => {
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
            window.removeEventListener('beforeunload', onBeforeUnload)
            releaseLock()
        }
    }, [acquireLock, releaseLock])

    // Enviar manual (sessionId no body)
    const enviar = useCallback(() => {
        const url = '/api/tvs/editTvTo'
        axios.post(url, { qtd, report, sessionId: sessionIdRef.current })
            .then(res => {
                alert(res.data)
                window.location = `/documentosPaf/${window.location.search.slice(1)}`
            })
            .catch(error => {
                if (error?.response?.status === 423) {
                    setLocked(true)
                    setLockedBy(error?.response?.data?.by || null)
                    setLockedUntil(error?.response?.data?.expiresAt ? new Date(error.response.data.expiresAt) : null)
                    showToast('warn', 'Este documento está sendo editado por outra pessoa.')
                    return
                }
                alert(error)
            })
    }, [qtd, report, showToast])

    // Autosave (pausa se locked) — sessionId no body
    const autoSalvar = useCallback(async () => {
        if (!readyRef.current) return
        if (lockedRef.current) return
        try {
            const url = '/api/tvs/editTvTo'
            await axios.post(url, { qtd: qtdRef.current, report: reportRef.current, sessionId: sessionIdRef.current })
            showToast('success', 'Documento salvo automaticamente.')
        } catch (err) {
            if (err?.response?.status === 423) {
                setLocked(true)
                setLockedBy(err?.response?.data?.by || null)
                setLockedUntil(err?.response?.data?.expiresAt ? new Date(err.response.data.expiresAt) : null)
                showToast('warn', 'Outro usuário assumiu a edição; salvamento pausado.')
                return
            }
            console.error(err)
            showToast('error', 'Falha ao salvar automaticamente.')
        }
    }, [showToast])

    useEffect(() => {
        const t = setInterval(() => { autoSalvar() }, AUTO_SAVE_MS)
        return () => clearInterval(t)
    }, [autoSalvar])

    return (
        <>
            <Toast toast={toast} />

            {locked && (
                <LockOverlay
                    by={lockedBy}
                    until={lockedUntil}
                    onRetry={() => acquireLock()}
                />
            )}

            <div aria-hidden={locked}>
                <DadosIniciais modulo="dadosIniciais" />
                <DadosEstabelecimento modulo="dadosEstabelecimento" />
                {RenderFicha()}
                <AiContainer />
                <button
                    className='btn btn-success mt-2'
                    onClick={e => enviar()}
                    disabled={locked}
                    title={locked ? 'Documento bloqueado por outro usuário' : 'Enviar alterações'}
                >
                    Enviar
                </button>
            </div>
        </>
    )

    function RenderFicha() {
        if (tipo === "Livre") return <Livre />
        else if (tipo === "Consultório") return <Consultorio />
        else if (tipo === "Caps") return <Caps />
        else if (tipo === "Clínica") return <Clinica />
        else if (tipo === "Nasf") return <Nasf />
        else if (tipo === "Exercício Ilegal") return <ExercicioIlegal />
        else if (tipo === "Rede Social") return <RedeSocial />
        else if (tipo === "ILPIS") return <Ilpis />
        else if (tipo === "Hospital") return <Hospital />
        else if (tipo === "CER") return <Cer />
        else if (tipo === "Fechado/Desativado") return <Fechado />
        return null
    }
}

export default EditTO

// --- Toast discreto ---
function Toast({ toast }) {
    if (!toast) return null
    const base = {
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        minWidth: 260,
        maxWidth: 360,
        padding: '10px 12px',
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        fontSize: 14,
        lineHeight: 1.25
    }
    const theme =
        toast.type === 'success' ? { background: '#e7f7ec', border: '1px solid #bfe8cc', color: '#0f5132' }
            : toast.type === 'warn' ? { background: '#fff4e5', border: '1px solid #ffd8a8', color: '#8b5e00' }
                : { background: '#fdecec', border: '1px solid #f5b5b5', color: '#842029' }
    return <div style={{ ...base, ...theme }}>{toast.text}</div>
}

// --- Overlay de bloqueio total ---
function LockOverlay({ by, until, onRetry }) {
    const base = {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
    const card = {
        background: '#fff',
        borderRadius: 12,
        padding: 20,
        boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
        minWidth: 320,
        maxWidth: 520
    }
    const title = { fontSize: 18, fontWeight: 700, marginBottom: 8 }
    const p = { margin: '6px 0', fontSize: 14, color: '#333' }
    const btn = {
        marginTop: 14,
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid #ccc',
        background: '#f7f7f7',
        cursor: 'pointer'
    }

    const nome = by?.nome || 'Outro usuário'
    const ate = until ? new Date(until).toLocaleTimeString('pt-BR', { hour12: false }) : null

    return (
        <div style={base} role="dialog" aria-modal="true">
            <div style={card}>
                <div style={title}>Documento em edição</div>
                <div style={p}>
                    {nome} está editando este documento no momento.
                    {ate ? <> Tente novamente após <b>{ate}</b>.</> : null}
                </div>
                <button style={btn} onClick={onRetry}>Tentar novamente</button>
            </div>
        </div>
    )
}
